import { useEffect, useRef, useState } from "react";
import { ClipboardList, Pencil, X, Minus, Plus, Trash2, HandCoins, MessageCircle, Search } from "lucide-react";
import { motion } from "framer-motion";
import { toast } from "sonner";
import api from "../lib/api";
import { ORDER_STATUSES, statusColor } from "../lib/orderStatus";
import { ProductModal } from "./ProductModal";

const euro = (n) => `€${Number(n).toFixed(2).replace(".", ",")}`;
const resolveImg = (src) => (!src ? null : src.startsWith("http") ? src : `${process.env.REACT_APP_BACKEND_URL}${src}`);

export const AdminOrders = ({ focusOrder, onFocusConsumed }) => {
  const [orders, setOrders] = useState(null);
  const [editing, setEditing] = useState(null);
  const [viewProduct, setViewProduct] = useState(null);
  const [q, setQ] = useState("");
  const [fStatus, setFStatus] = useState("all");
  const [products, setProducts] = useState([]);

  const load = () => api.get("/admin/orders").then(({ data }) => setOrders(data)).catch(() => setOrders([]));
  useEffect(() => {
    load();
    api.get("/products").then(({ data }) => setProducts(data)).catch(() => {});
  }, []);
  const imgFor = (id) => products.find((p) => p.id === id)?.image;

  useEffect(() => {
    if (focusOrder && orders) {
      const o = orders.find((x) => x.id === focusOrder);
      if (o) openEdit(o);
      onFocusConsumed?.();
    }
    // eslint-disable-next-line
  }, [focusOrder, orders]);

  const openProduct = async (id) => {
    try {
      const { data } = await api.get(`/products/${id}`);
      setViewProduct(data);
    } catch { toast.error("Prodotto non più a catalogo"); }
  };

  const openEdit = (o) => {
    setEditing({ ...o, address: o.address || { via: "", citta: "", cap: "", telefono: "" }, internal_notes: o.internal_notes || "" });
    if ((o.unread_admin || 0) > 0) {
      api.post(`/admin/orders/${o.id}/read`).catch(() => {});
      setOrders((prev) => prev.map((x) => (x.id === o.id ? { ...x, unread_admin: 0 } : x)));
    }
  };

  const setStatus = async (id, status) => {
    try {
      await api.put(`/admin/orders/${id}/status`, { status });
      setOrders((prev) => prev.map((o) => (o.id === id ? { ...o, status } : o)));
      toast.success("Stato aggiornato, email inviata al cliente");
    } catch { toast.error("Aggiornamento non riuscito"); }
  };

  if (orders === null) return null;

  const filtered = orders.filter((o) => {
    if (fStatus !== "all" && o.status !== fStatus) return false;
    if (q.trim()) {
      const s = q.toLowerCase();
      const hay = `${o.email || ""} ${o.items.map((i) => i.name).join(" ")}`.toLowerCase();
      if (!hay.includes(s)) return false;
    }
    return true;
  });

  return (
    <section className="mt-2" data-testid="admin-orders">
      <div className="mb-4 flex items-center gap-2">
        <ClipboardList className="h-5 w-5 text-terra" strokeWidth={1.6} />
        <h2 className="font-serif text-2xl font-light text-ink">Ordini</h2>
        <span className="text-sm text-ink-soft" data-testid="admin-order-count">({orders.length})</span>
        {orders.some((o) => ["confermato", "composto"].includes(o.status)) && (
          <span className="flex items-center gap-1.5 rounded-full bg-terra/15 px-3 py-1 text-xs font-semibold text-terra" data-testid="new-orders-badge">
            <span className="h-1.5 w-1.5 animate-pulse rounded-full bg-terra" />
            {orders.filter((o) => ["confermato", "composto"].includes(o.status)).length} da evadere
          </span>
        )}
      </div>
      <div className="mb-4 flex flex-wrap gap-2" data-testid="orders-filters">
        <div className="relative">
          <Search className="absolute left-3.5 top-1/2 h-4 w-4 -translate-y-1/2 text-ink-soft" />
          <input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Cerca cliente o prodotto…" data-testid="orders-search" className="w-64 rounded-full border border-ink/15 bg-panel py-2.5 pl-10 pr-4 text-sm text-ink outline-none focus:border-terra" />
        </div>
        <select value={fStatus} onChange={(e) => setFStatus(e.target.value)} data-testid="orders-status-filter" className="rounded-full border border-ink/15 bg-panel px-4 py-2.5 text-sm text-ink outline-none focus:border-terra">
          <option value="all">Tutti gli stati</option>
          {ORDER_STATUSES.map((s) => <option key={s.value} value={s.value}>{s.label}</option>)}
        </select>
      </div>
      {orders.length === 0 ? (
        <p className="rounded-2xl border border-dashed border-ink/15 bg-panel p-8 text-center text-ink-soft" data-testid="admin-no-orders">Nessun ordine ricevuto.</p>
      ) : (
        <div className="space-y-3">
          {filtered.length === 0 && <p className="rounded-2xl border border-dashed border-ink/15 bg-panel p-8 text-center text-ink-soft">Nessun ordine corrisponde ai filtri.</p>}
          {filtered.map((o) => (
            <div
              key={o.id}
              onClick={(e) => { if (!e.target.closest("select,button")) openEdit(o); }}
              className={`cursor-pointer rounded-2xl border bg-panel p-4 transition-colors hover:border-terra/50 ${["confermato", "composto"].includes(o.status) ? "border-terra/40 ring-1 ring-terra/20" : "border-ink/10"}`}
              data-testid={`admin-order-${o.id}`}
            >
              <div className="flex items-start justify-between gap-3">
                <p className="min-w-0 font-medium text-ink">
                  {new Date(o.created_at).toLocaleDateString("it-IT", { day: "2-digit", month: "short", year: "numeric" })}
                  {o.plan === "box_mensile" && <span className="ml-2 rounded-full border border-sage/40 bg-sage/15 px-2.5 py-0.5 text-xs font-medium text-sage">Box</span>}
                  {o.refunded && <span className="ml-2 rounded-full border border-amber/40 bg-amber/15 px-2.5 py-0.5 text-xs font-medium text-amber">Rimborsato</span>}
                  {(o.unread_admin || 0) > 0 && (
                    <span className="ml-2 inline-flex items-center gap-1 rounded-full border border-terra/50 bg-terra/20 px-2.5 py-0.5 text-xs font-bold text-terra" data-testid={`unread-badge-${o.id}`}>
                      <MessageCircle className="h-3 w-3" /> {o.unread_admin}
                    </span>
                  )}
                </p>
                <span className="shrink-0 font-serif text-lg text-ink">{euro(o.total)}</span>
              </div>
              <div className="mt-2 space-y-1">
                {o.items.map((i, idx) => (
                  <button key={idx} onClick={() => openProduct(i.id)} data-testid={`order-item-${o.id}-${idx}`} className="flex w-full items-center gap-3 rounded-lg px-1 py-1 text-left transition-colors hover:bg-ink/5">
                    {imgFor(i.id) ? (
                      <img src={resolveImg(imgFor(i.id))} alt="" className="h-10 w-10 shrink-0 rounded-lg object-cover" />
                    ) : (
                      <span className="grid h-10 w-10 shrink-0 place-items-center rounded-lg bg-cream text-[10px] text-ink-soft">—</span>
                    )}
                    <span className="min-w-0 flex-1 truncate text-sm text-ink">{i.name}</span>
                    <span className="shrink-0 text-xs text-ink-soft">×{i.qty}</span>
                  </button>
                ))}
              </div>
              <p className="mt-2 truncate text-xs text-ink-soft">
                {o.customer_name ? `${o.customer_name} · ` : ""}{o.email || "—"}{o.address?.via ? ` · ${o.address.via}${o.address.civico ? ` ${o.address.civico}` : ""}, ${o.address.citta}` : ""}
              </p>
              <div className="mt-3 flex items-center gap-2">
                <select
                  value={o.status}
                  onChange={(e) => setStatus(o.id, e.target.value)}
                  data-testid={`order-status-${o.id}`}
                  className={`min-w-0 flex-1 appearance-none rounded-full border px-4 py-2 text-sm font-medium outline-none ${statusColor[o.status] || "bg-white/10 text-ink border-white/25"}`}
                >
                  {ORDER_STATUSES.map((s) => <option key={s.value} value={s.value}>{s.label}</option>)}
                </select>
                <button onClick={() => openEdit(o)} data-testid={`edit-order-${o.id}`} className="grid h-9 w-9 shrink-0 place-items-center rounded-full text-ink hover:bg-ink/5">
                  <Pencil className="h-4 w-4" />
                </button>
              </div>
            </div>
          ))}
        </div>
      )}
      {editing && <OrderEditDrawer order={editing} onClose={() => setEditing(null)} onSaved={() => { setEditing(null); load(); }} onViewProduct={openProduct} imgFor={imgFor} />}
      {viewProduct && <ProductModal product={viewProduct} onClose={() => setViewProduct(null)} />}
    </section>
  );
};

const OrderEditDrawer = ({ order, onClose, onSaved, onViewProduct, imgFor }) => {
  const [form, setForm] = useState(order);
  const [saving, setSaving] = useState(false);
  const [refunding, setRefunding] = useState(false);
  const [msgText, setMsgText] = useState("");
  const [msgVisible, setMsgVisible] = useState(false);
  const [sendingMsg, setSendingMsg] = useState(false);
  const [addrOpen, setAddrOpen] = useState(false);
  const chatRef = useRef(null);

  useEffect(() => {
    chatRef.current?.scrollTo({ top: chatRef.current.scrollHeight });
  }, [form.messages?.length]);

  const sendMessage = async () => {
    setSendingMsg(true);
    try {
      const { data } = await api.post(`/admin/orders/${order.id}/messages`, { text: msgText, visible_to_customer: msgVisible });
      setForm({ ...form, messages: data.messages });
      setMsgText("");
      setMsgVisible(false);
      toast.success(msgVisible ? "Messaggio inviato al cliente (email di avviso spedita)" : "Nota interna salvata");
    } catch (e) { toast.error(e.response?.data?.detail || "Invio non riuscito"); } finally { setSendingMsg(false); }
  };

  const setItem = (idx, qty) => {
    const items = form.items.map((it, i) => (i === idx ? { ...it, qty } : it)).filter((it) => it.qty > 0);
    setForm({ ...form, items, total: round2(items.reduce((s, it) => s + it.price * it.qty, 0)) });
  };
  const round2 = (n) => Math.round(n * 100) / 100;

  const save = async () => {
    setSaving(true);
    try {
      await api.put(`/admin/orders/${order.id}`, {
        items: form.items, total: form.total, status: form.status,
        address: form.address?.via ? form.address : null,
      });
      toast.success("Ordine aggiornato");
      onSaved();
    } catch (e) { toast.error(e.response?.data?.detail || "Salvataggio non riuscito"); } finally { setSaving(false); }
  };

  const refund = async () => {
    if (!window.confirm(`Accreditare ${euro(form.total)} come credito al cliente?`)) return;
    setRefunding(true);
    try {
      await api.post(`/admin/orders/${order.id}/refund`, {});
      toast.success("Credito rimborsato al cliente");
      onSaved();
    } catch (e) { toast.error(e.response?.data?.detail || "Rimborso non riuscito"); } finally { setRefunding(false); }
  };

  return (
    <div className="fixed inset-0 z-50 flex justify-end bg-ink/40 backdrop-blur-sm" onClick={onClose}>
      <motion.div
        initial={{ x: "100%" }} animate={{ x: 0 }} transition={{ type: "spring", damping: 30, stiffness: 300 }}
        onClick={(e) => e.stopPropagation()}
        className="safe-pt safe-pb flex h-full w-full max-w-2xl flex-col overflow-y-auto bg-cream p-6 md:p-8"
        data-testid="order-edit-drawer"
      >
        <div className="mb-6 flex items-center justify-between">
          <h2 className="font-serif text-2xl text-ink">Modifica ordine</h2>
          <button onClick={onClose} className="grid h-9 w-9 place-items-center rounded-full hover:bg-ink/10"><X className="h-5 w-5" /></button>
        </div>

        <p className="mb-1 text-xs uppercase tracking-overline text-ink-soft">Prodotti</p>
        <div className="mb-5 space-y-2">
          {form.items.map((it, idx) => (
            <div key={idx} className="rounded-xl border border-ink/10 bg-panel" data-testid={`edit-item-${idx}`}>
              <div className="flex items-center gap-3 px-3 py-2">
                {imgFor?.(it.id) ? (
                  <img src={resolveImg(imgFor(it.id))} alt="" className="h-10 w-10 shrink-0 rounded-lg object-cover" />
                ) : (
                  <span className="grid h-10 w-10 shrink-0 place-items-center rounded-lg bg-cream text-[10px] text-ink-soft">—</span>
                )}
                <button onClick={() => onViewProduct(it.id)} data-testid={`edit-item-detail-${idx}`} className="min-w-0 flex-1 truncate text-left text-sm font-medium text-ink underline decoration-ink/30 decoration-dotted underline-offset-4 hover:text-terra">
                  {it.name} <span className="text-ink-soft">({euro(it.price)})</span>
                </button>
                <div className="flex items-center gap-2 rounded-full border border-ink/15 px-2 py-1">
                  <button onClick={() => setItem(idx, it.qty - 1)} data-testid={`edit-dec-${idx}`} className="text-ink hover:text-sage"><Minus className="h-3.5 w-3.5" /></button>
                  <span className="w-4 text-center text-sm" data-testid={`edit-qty-${idx}`}>{it.qty}</span>
                  <button onClick={() => setItem(idx, it.qty + 1)} data-testid={`edit-inc-${idx}`} className="text-ink hover:text-sage"><Plus className="h-3.5 w-3.5" /></button>
                </div>
                <button onClick={() => setItem(idx, 0)} data-testid={`edit-del-${idx}`} className="text-red-600 hover:opacity-70"><Trash2 className="h-4 w-4" /></button>
              </div>
            </div>
          ))}
        </div>

        <label className="mb-5 block">
          <span className="text-xs uppercase tracking-overline text-ink-soft">Totale (€)</span>
          <input type="number" step="0.01" min="0" value={form.total} onChange={(e) => setForm({ ...form, total: parseFloat(e.target.value) || 0 })} data-testid="edit-total" className="mt-1.5 w-full rounded-xl border border-ink/15 bg-panel px-4 py-2.5 text-sm outline-none focus:border-terra" />
        </label>

        <label className="mb-5 block">
          <span className="text-xs uppercase tracking-overline text-ink-soft">Stato</span>
          <select value={form.status} onChange={(e) => setForm({ ...form, status: e.target.value })} data-testid="edit-status" className="mt-1.5 w-full rounded-xl border border-ink/15 bg-panel px-4 py-2.5 text-sm outline-none focus:border-terra">
            {ORDER_STATUSES.map((s) => <option key={s.value} value={s.value}>{s.label}</option>)}
          </select>
        </label>

        <p className="mb-1.5 text-xs uppercase tracking-overline text-ink-soft">Indirizzo di spedizione</p>
        {!addrOpen ? (
          <div className="mb-5 flex items-center gap-3 rounded-xl border border-ink/10 bg-panel px-4 py-3" data-testid="edit-address-summary">
            <p className="min-w-0 flex-1 truncate text-sm text-ink" data-testid="edit-address-summary-text">
              {form.address?.via
                ? `${form.address.via}${form.address.civico ? ` ${form.address.civico}` : ""}, ${form.address.cap} ${form.address.citta}${form.address.provincia ? ` (${form.address.provincia})` : ""}${form.address.telefono ? ` · Tel. ${form.address.telefono}` : ""}`
                : "Nessun indirizzo salvato sull'ordine"}
            </p>
            <button onClick={() => setAddrOpen(true)} data-testid="edit-address-open" className="shrink-0 text-xs font-semibold text-sage hover:underline">Modifica</button>
          </div>
        ) : (
        <div className="mb-5 space-y-2" data-testid="edit-address-form">
          <div className="flex justify-end">
            <button onClick={() => setAddrOpen(false)} data-testid="edit-address-close" className="text-xs font-semibold text-sage hover:underline">Chiudi e riduci</button>
          </div>
          <div className="flex gap-2">
            <input value={form.address.via} onChange={(e) => setForm({ ...form, address: { ...form.address, via: e.target.value } })} placeholder="Via" data-testid="edit-addr-via" className="flex-1 rounded-xl border border-ink/15 bg-panel px-4 py-2.5 text-sm outline-none focus:border-terra" />
            <input value={form.address.civico || ""} onChange={(e) => setForm({ ...form, address: { ...form.address, civico: e.target.value } })} placeholder="N. civico" data-testid="edit-addr-civico" className="w-24 rounded-xl border border-ink/15 bg-panel px-4 py-2.5 text-sm outline-none focus:border-terra" />
          </div>
          <div className="flex gap-2">
            <input value={form.address.citta} onChange={(e) => setForm({ ...form, address: { ...form.address, citta: e.target.value } })} placeholder="Comune di residenza" data-testid="edit-addr-citta" className="flex-1 rounded-xl border border-ink/15 bg-panel px-4 py-2.5 text-sm outline-none focus:border-terra" />
            <input value={form.address.cap} onChange={(e) => setForm({ ...form, address: { ...form.address, cap: e.target.value } })} placeholder="CAP" data-testid="edit-addr-cap" className="w-24 rounded-xl border border-ink/15 bg-panel px-4 py-2.5 text-sm outline-none focus:border-terra" />
          </div>
          <input value={form.address.provincia || ""} onChange={(e) => setForm({ ...form, address: { ...form.address, provincia: e.target.value } })} placeholder="Provincia" data-testid="edit-addr-provincia" className="w-full rounded-xl border border-ink/15 bg-panel px-4 py-2.5 text-sm outline-none focus:border-terra" />
          <input value={form.address.telefono || ""} onChange={(e) => setForm({ ...form, address: { ...form.address, telefono: e.target.value } })} placeholder="Telefono" data-testid="edit-addr-tel" className="w-full rounded-xl border border-ink/15 bg-panel px-4 py-2.5 text-sm outline-none focus:border-terra" />
        </div>
        )}

        <p className="mb-1.5 text-xs uppercase tracking-overline text-ink-soft">Chat ordine — note interne e messaggi al cliente</p>
        <div ref={chatRef} className="mb-3 h-72 shrink-0 space-y-2 overflow-y-auto rounded-xl border border-ink/10 bg-panel p-3" data-testid="order-messages">
          {(!form.messages || form.messages.length === 0) && !form.internal_notes && (
            <p className="py-2 text-center text-xs text-ink-soft">Nessun messaggio. Scrivi qui sotto: le note restano interne, oppure spunta "Visibile al cliente" per inviargliele (con email di avviso).</p>
          )}
          {form.internal_notes && (
            <div className="max-w-[85%] rounded-xl rounded-br-sm border border-dashed border-amber-400 bg-amber-50 px-3 py-2 text-sm text-ink">
              <p>{form.internal_notes}</p>
              <p className="mt-0.5 text-[10px] uppercase tracking-wide text-amber-600">Nota interna · solo admin</p>
            </div>
          )}
          {(form.messages || []).map((m) => (
            <div key={m.id} className={`max-w-[85%] rounded-xl px-3 py-2 text-sm ${m.author === "admin" ? "ml-auto rounded-br-sm bg-ink text-cream" : "rounded-bl-sm bg-sage/15 text-ink"}`} data-testid={`msg-${m.id}`}>
              <p>{m.text}</p>
              <p className={`mt-0.5 text-[10px] uppercase tracking-wide ${m.author === "admin" ? "text-cream/60" : "text-sage"}`}>
                {m.author === "admin" ? (m.visible_to_customer ? "Tu · visibile al cliente" : "Tu · nota interna") : "Cliente"} · {new Date(m.created_at).toLocaleDateString("it-IT", { day: "2-digit", month: "short" })} {new Date(m.created_at).toLocaleTimeString("it-IT", { hour: "2-digit", minute: "2-digit" })}
              </p>
            </div>
          ))}
        </div>
        <div className="mb-6 space-y-2">
          <textarea rows={2} value={msgText} onChange={(e) => setMsgText(e.target.value)} placeholder="Scrivi una nota o un messaggio…" data-testid="order-message-input" className="w-full resize-none rounded-xl border border-ink/15 bg-panel px-4 py-2.5 text-sm outline-none focus:border-terra" />
          <div className="flex items-center justify-between gap-3">
            <label className="flex cursor-pointer items-center gap-2 text-xs text-ink" data-testid="msg-visible-label">
              <input type="checkbox" checked={msgVisible} onChange={(e) => setMsgVisible(e.target.checked)} data-testid="msg-visible-toggle" className="h-4 w-4 accent-terra" />
              Visibile al cliente (con email di avviso)
            </label>
            <button onClick={sendMessage} disabled={sendingMsg || !msgText.trim()} data-testid="send-order-message" className="rounded-full bg-terra px-5 py-2 text-xs font-semibold text-cream hover:opacity-90 disabled:opacity-50">
              {sendingMsg ? "Invio…" : "Invia"}
            </button>
          </div>
        </div>

        <button onClick={save} disabled={saving} data-testid="save-order-button" className="w-full rounded-full bg-ink py-3.5 text-sm font-semibold text-cream hover:bg-sage disabled:opacity-60">
          {saving ? "Salvataggio…" : "Salva modifiche"}
        </button>
        {!order.refunded && order.user_id && (
          <button onClick={refund} disabled={refunding} data-testid="refund-order-button" className="mt-3 flex w-full items-center justify-center gap-2 rounded-full border border-amber-300 bg-amber-50 py-3 text-sm font-semibold text-amber-700 hover:bg-amber-100 disabled:opacity-60">
            <HandCoins className="h-4 w-4" /> {refunding ? "Rimborso…" : `Rimborsa ${euro(order.total)} come credito`}
          </button>
        )}
      </motion.div>
    </div>
  );
};
